import { useRef, useCallback } from 'react'
import type { ChatFile } from '../../api/client'

const MAX_SIZE = 25 * 1024 * 1024
const ALLOWED_TYPES = new Set(['text/plain', 'text/markdown', 'text/csv'])
const ALLOWED_EXTS = ['.txt', '.md', '.csv']

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

interface FileAttachControlProps {
  files: ChatFile[]
  onUpload: (file: File) => Promise<void>
  onRemove: (fileId: string) => void
  disabled?: boolean
}

export function FileAttachControl({ files, onUpload, onRemove, disabled }: FileAttachControlProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (inputRef.current) inputRef.current.value = ''
    if (!file) return

    if (file.size > MAX_SIZE) {
      alert(`File too large: ${formatBytes(file.size)}. Maximum is 25 MB.`)
      return
    }
    const ext = '.' + (file.name.split('.').pop() ?? '').toLowerCase()
    const typeOk = ALLOWED_TYPES.has(file.type) || ALLOWED_EXTS.includes(ext)
    if (!typeOk) {
      alert(`Unsupported file type "${file.type}". Supported: .txt, .md, .csv`)
      return
    }

    try {
      await onUpload(file)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Upload failed.')
    }
  }, [onUpload])

  return (
    <div className="space-y-1">
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-1">
          {files.map(f => (
            <span
              key={f.id}
              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200"
            >
              <span className="truncate max-w-[100px]" title={f.filename}>{f.filename}</span>
              <span className="text-gray-400">({formatBytes(f.size_bytes)})</span>
              <button
                onClick={() => onRemove(f.id)}
                className="text-gray-400 hover:text-red-500 leading-none ml-0.5"
                title="Remove"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div>
        <input
          ref={inputRef}
          type="file"
          accept=".txt,.md,.csv,text/plain,text/markdown,text/csv"
          className="hidden"
          onChange={e => { void handleFileChange(e) }}
          disabled={disabled}
        />
        <button
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          className="text-xs text-gray-400 hover:text-indigo-600 px-1.5 py-0.5 rounded hover:bg-indigo-50 transition-colors disabled:opacity-40"
          title="Attach file (.txt, .md, .csv — max 25 MB)"
          type="button"
        >
          Attach
        </button>
      </div>
    </div>
  )
}
